// Seeding the design patterns course (docs/areas/learn.md): one Course, three Modules, one Lesson
// and one Concept per pattern. Ids are derived from the suite and the pattern, so seeding twice (or
// from two devices) lands on the same records and never doubles them.
import { v5 as uuidv5 } from 'uuid'
import { db as runtimeDb, mutate as runtimeMutate } from '@/runtime'
import type { Db } from '@/db/schema'
import type { Mutator } from '@/db/ops'
import { WS } from '@/db/ops'
import { DEFAULT_WORKSPACE_SLUG } from '@/kinds/workspace/schema'
import { TRACK } from '@/kinds/learn/schema'
import { useSeeding } from '@/state/learnSeeding'
import { PATTERNS, PATTERN_CATEGORIES, RELATED, patternUrl } from './learnPatterns'
import { COMBOS, TEACHING } from './learnContent'
import type { Quiz } from './learnContent/types'

export const DESIGN_PATTERNS_TRACK = 'design-patterns'

/** A fixed namespace: only ever used to name seed records. */
const NAMESPACE = '6f1c2a54-3f0b-4d7e-9a58-2c1b7e4d9a10'

export const seedId = (ws: string, key: string) => uuidv5(`${ws}:${DESIGN_PATTERNS_TRACK}:${key}`, NAMESPACE)

export interface SeedResult {
  /** records written or brought back; 0 when everything was already there */
  made: number
}

/**
 * Adds the course to the suite `ws` as one undoable step. A record already there is left alone; one
 * in the Trash is brought back; an edge already linked is not linked twice.
 */
export const seedDesignPatterns = async (
  ws: string = DEFAULT_WORKSPACE_SLUG,
  db: Db = runtimeDb,
  mutate: Mutator = runtimeMutate,
): Promise<SeedResult> => {
  useSeeding.getState().set(true)
  try {
    return await seed(ws, db, mutate)
  } finally {
    useSeeding.getState().set(false)
  }
}

const seed = async (ws: string, db: Db, mutate: Mutator): Promise<SeedResult> => {
  const labels = { [WS]: ws, [TRACK]: DESIGN_PATTERNS_TRACK }
  let made = 0
  const ensure = async (id: string, kind: string, spec: Record<string, unknown>) => {
    const have = await db.resources.get(id)
    if (have && !have.deleted_at) return
    if (have) await mutate.restore(id)
    else await mutate.create(kind, spec, labels, id)
    made++
  }
  const link = async (
    from: string,
    to: string,
    relation: 'has_module' | 'has_lesson' | 'teaches' | 'related_to' | 'exercises',
  ) => {
    const have = (await db.edges.where('from_id').equals(from).toArray()).find(
      (e) => e.to_id === to && e.relation === relation && !e.deleted_at,
    )
    if (!have) await mutate.setEdge(from, to, relation)
  }
  await mutate.grouped(async () => {
    const course = seedId(ws, 'course')
    await ensure(course, 'Course', {
      title: 'Design patterns',
      source_url: 'https://refactoring.guru/design-patterns',
      goal: 'Name the patterns I already use and spot them, or their misuse, in a diff.',
    })
    for (const [m, category] of PATTERN_CATEGORIES.entries()) {
      const module = seedId(ws, `module:${category}`)
      await ensure(module, 'Module', { title: category, order: m })
      await link(course, module, 'has_module')
      for (const [i, p] of PATTERNS.filter((x) => x.category === category).entries()) {
        const lesson = seedId(ws, `lesson:${p.slug}`)
        const concept = seedId(ws, `concept:${p.slug}`)
        await ensure(lesson, 'Lesson', {
          title: p.name,
          order: i,
          url: patternUrl(p.slug),
          estimate_minutes: 10,
          kind_hint: 'read',
        })
        await ensure(concept, 'Concept', {
          name: p.name,
          category,
          one_liner: p.oneLiner,
          source_url: patternUrl(p.slug),
          my_notes: p.where,
        })
        await link(module, lesson, 'has_lesson')
        await link(lesson, concept, 'teaches')
      }
    }
    for (const [a, b] of RELATED)
      await link(seedId(ws, `concept:${a}`), seedId(ws, `concept:${b}`), 'related_to')

    // the teaching: Learn cards and questions on each concept, then the combinations over several
    const exercise = async (key: string, spec: Record<string, unknown>, concepts: string[]) => {
      const id = seedId(ws, `exercise:${key}`)
      await ensure(id, 'Exercise', { ...spec, concept_ref: concepts[0], generated_by: 'seed' })
      for (const c of concepts) await link(id, c, 'exercises')
    }
    const quizSpec = (q: Quiz) => ({
      format: q.format,
      prompt: q.prompt,
      ...(q.code ? { code_excerpt: q.code } : {}),
      choices: q.choices.map((c) => (c.correct ? { label: c.label, correct: true } : { label: c.label })),
      ...(q.multi ? { multi_select: true } : {}),
      explanation: q.explanation,
    })
    for (const t of TEACHING) {
      const concept = seedId(ws, `concept:${t.slug}`)
      // what the flip side of a Learn card shows, unless he has already written his own
      const have = await db.resources.get(concept)
      const spec = (have?.spec ?? {}) as { when_to_use?: string; when_not_to?: string }
      if (have && !have.deleted_at && (!spec.when_to_use || !spec.when_not_to))
        await mutate.patch(concept, {
          spec: {
            ...(spec.when_to_use ? {} : { when_to_use: t.whenToUse }),
            ...(spec.when_not_to ? {} : { when_not_to: t.whenNotTo }),
          },
        })
      for (const [i, c] of t.teach.entries())
        await exercise(
          `${t.slug}:learn:${i}`,
          { format: 'learn', prompt: `${c.label}\n${c.text}`, order: i },
          [concept],
        )
      for (const [i, q] of t.quizzes.entries())
        await exercise(`${t.slug}:${q.format}:${i}`, quizSpec(q), [concept])
    }
    for (const c of COMBOS) {
      const concepts = c.slugs.map((slug) => seedId(ws, `concept:${slug}`))
      for (const [i, card] of c.teach.entries())
        await exercise(
          `combo:${c.key}:learn:${i}`,
          { format: 'learn', prompt: `${card.label}\n${card.text}`, order: i },
          concepts,
        )
      for (const [i, q] of c.quizzes.entries())
        await exercise(`combo:${c.key}:${q.format}:${i}`, quizSpec(q), concepts)
    }
  })
  return { made }
}

/**
 * The ids of every Exercise the seed makes for a suite (UUIDv5 of the same keys `seedDesignPatterns` uses), so
 * the home can tell a course seeded before the latest questions from a complete one.
 */
export const teachingExerciseIds = (ws: string): string[] => [
  ...TEACHING.flatMap((t) => [
    ...t.teach.map((_, i) => seedId(ws, `exercise:${t.slug}:learn:${i}`)),
    ...t.quizzes.map((q, i) => seedId(ws, `exercise:${t.slug}:${q.format}:${i}`)),
  ]),
  ...COMBOS.flatMap((c) => [
    ...c.teach.map((_, i) => seedId(ws, `exercise:combo:${c.key}:learn:${i}`)),
    ...c.quizzes.map((q, i) => seedId(ws, `exercise:combo:${c.key}:${q.format}:${i}`)),
  ]),
]
